/**
 * Order and fulfillment-order amounts under the separated shipping-charge contract (#72).
 * The server serves that contract because this app sends `X-Client-Contracts: shipping-charge-v1`
 * (lib/api.ts). A response carrying `shippingCharge` / `shippingCharges` is the new contract;
 * otherwise it is legacy (the first order's `totalAmount` includes the parcel's shipping fee).
 * Fields of the two contracts are never combined in one amount. Same rules as the mobile app's
 * `shared/utils/order/resolveOrderShippingAmounts.ts`.
 */

type Money = { amount: number }

type ShippingChargeAmounts = {
  amount: Money
  remainingAmount: Money
  isRefundable?: boolean
}

type OrderAmountSource = {
  totalAmount: Money
  shippingCharge?: ShippingChargeAmounts | null
}

type FulfillmentOrderAmountSource = {
  shippingFee: Money
  shippingFeeRefundedAt?: string | null
  shippingCharges?: ShippingChargeAmounts[]
  orders: OrderAmountSource[]
}

/** What this order paid: products plus the shipping charge it paid (new contract). */
export function getOrderPaidAmount(order: OrderAmountSource): number {
  if (order.shippingCharge === undefined) return order.totalAmount.amount
  return order.totalAmount.amount + (order.shippingCharge?.amount.amount ?? 0)
}

/** Shipping fee of the whole fulfillment order. */
export function getFulfillmentOrderShippingFee(fo: FulfillmentOrderAmountSource): number {
  if (fo.shippingCharges === undefined) return fo.shippingFee.amount
  return fo.shippingCharges.reduce((sum, charge) => sum + charge.amount.amount, 0)
}

/** Product amount of the whole fulfillment order, or null for legacy responses. */
export function getFulfillmentOrderProductAmount(fo: FulfillmentOrderAmountSource): number | null {
  if (fo.shippingCharges === undefined) return null
  return fo.orders.reduce((sum, order) => sum + order.totalAmount.amount, 0)
}

/** Everything paid for the fulfillment order: products plus its shipping fee. */
export function getFulfillmentOrderPaidAmount(fo: FulfillmentOrderAmountSource): number {
  const productAmount = getFulfillmentOrderProductAmount(fo)
  if (productAmount === null) {
    return fo.orders.reduce((sum, order) => sum + order.totalAmount.amount, 0)
  }
  return productAmount + getFulfillmentOrderShippingFee(fo)
}

export function isFulfillmentOrderShippingFeeRefunded(fo: FulfillmentOrderAmountSource): boolean {
  if (fo.shippingCharges === undefined) return fo.shippingFeeRefundedAt != null
  return (
    fo.shippingCharges.length > 0 &&
    fo.shippingCharges.every((charge) => charge.remainingAmount.amount === 0)
  )
}

/**
 * Whether the shipping-fee refund button belongs on this member order. Under the separated
 * contract only the order that paid a charge with a remaining amount gets it; legacy responses
 * cannot tell which order paid, so the fulfillment-order rule applies to every member.
 */
export function canRefundOrderShippingFee(
  fo: FulfillmentOrderAmountSource,
  order: OrderAmountSource,
): boolean {
  if (fo.shippingCharges === undefined) return canRefundFulfillmentOrderShippingFee(fo)
  const charge = order.shippingCharge
  return charge != null && charge.remainingAmount.amount > 0 && charge.isRefundable !== false
}

/** Whether a shipping-fee refund can be offered for the fulfillment order. */
export function canRefundFulfillmentOrderShippingFee(fo: FulfillmentOrderAmountSource): boolean {
  if (fo.shippingCharges === undefined) {
    return fo.shippingFee.amount > 0 && fo.shippingFeeRefundedAt == null
  }
  // The server decides per charge; only an explicit `false` hides the refund.
  return fo.shippingCharges.some(
    (charge) => charge.remainingAmount.amount > 0 && charge.isRefundable !== false,
  )
}
